import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import ProjectDetailModal from "../../components/ProjectDetailModal";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";

interface Project {
  _id: string;
  title: string;
  description: string;
  difficulty: 'Easy' | 'Medium' | 'Hard';
  age: string;
  category: string;
  platform: string;
  bannerImage: string;
  isActive: boolean;
  hasPdf?: boolean;
  level?: number;
  technologies?: string[];
  hardware?: string[];
}

const CategoryDetail: React.FC = () => {
  const { category } = useParams<{ category: string }>();
  const [pdfOnly, setPdfOnly] = useState(false);
  const [difficultyLevels, setDifficultyLevels] = useState<number[]>([]);
  const [selectedTechnologies, setSelectedTechnologies] = useState<string[]>([]);
  const [selectedHardware, setSelectedHardware] = useState<string[]>([]);
  const [isDifficultyExpanded, setIsDifficultyExpanded] = useState(true);
  const [isTechnologyExpanded, setIsTechnologyExpanded] = useState(true);
  const [isHardwareExpanded, setIsHardwareExpanded] = useState(true);
  const [selectedProject, setSelectedProject] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fetch projects from API
  useEffect(() => {
    const fetchProjects = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:3030'}/highcode/projects`);
        
        if (!response.ok) {
          throw new Error('Failed to fetch projects');
        }
        
        const data = await response.json();
        setProjects(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load projects');
        console.error('Error fetching projects:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProjects();
  }, []);

  // Array of available banner images
  const bannerImages = [
    '/images/projects/astropi-ndvi-banner.png',
    '/images/projects/blockscad-bug-banner.png',
    '/images/projects/butterfly-banner.png',
    '/images/projects/cats-banner.png',
    '/images/projects/cats-vs-dogs-banner.png',
    '/images/projects/cd-sebento-htmlcss-1-banner.png',
    '/images/projects/cd-sebento-htmlcss-2-banner.png',
    '/images/projects/cd-sebento-htmlcss-3-banner.png',
    '/images/projects/count-creatures-banner.png',
    '/images/projects/cress-egg-heads-banner.png',
    '/images/projects/decision-tree-banner.png',
    '/images/projects/grow-dragonfly-banner.png',
    '/images/projects/grow-tree-introduction.png',
    '/images/projects/infrared-bird-box-banner.png',
    '/images/projects/led-firefly-banner.png',
    '/images/projects/nature-rover-banner.png',
    '/images/projects/popular-pets-banner.png',
    '/images/projects/sense-hat-data-logger-banner.png',
    '/images/projects/serene-scene-banner.png',
    '/images/projects/sprite-needs-you-banner.png',
    '/images/projects/sunrise-banner.png',
    '/images/projects/swarms-schools-flocks-banner.png',
    '/images/projects/tree-life-simulator-banner.png',
    '/images/projects/turtle-race-banner.png',
    '/images/projects/turtle-snowflakes-banner.png',
    '/images/projects/turtle-tracker-banner.png',
    '/images/projects/welcome-antarctica-banner.png',
    '/images/projects/world-environment-day-banner.png'
  ];

  // Function to get a banner image based on project ID
  const getBannerImage = (projectId: number) => {
    const index = projectId % bannerImages.length;
    return bannerImages[index];
  };

  // Projects are now fetched from API

  const handleDifficultyChange = (level: number) => {
    setDifficultyLevels(prev => 
      prev.includes(level) 
        ? prev.filter(l => l !== level)
        : [...prev, level]
    );
  };

  const handleTechnologyChange = (tech: string) => {
    setSelectedTechnologies(prev => 
      prev.includes(tech) 
        ? prev.filter(t => t !== tech)
        : [...prev, tech]
    );
  };

  const handleHardwareChange = (hardware: string) => {
    setSelectedHardware(prev => 
      prev.includes(hardware) 
        ? prev.filter(h => h !== hardware)
        : [...prev, hardware]
    );
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProject(null);
  };

  // Show loading spinner while fetching
  if (isLoading) {
    return <LoadingSpinner />;
  }

  // Show error if projects failed to load
  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Failed to Load Projects</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const filteredProjects = projects.filter(project => {
    // Filter by category if specified
    if (category && project.category.toLowerCase() !== category.toLowerCase()) return false;
    
    if (pdfOnly && !project.hasPdf) return false;
    if (difficultyLevels.length > 0 && project.level && !difficultyLevels.includes(project.level)) return false;
    if (selectedTechnologies.length > 0 && !selectedTechnologies.some(tech => project.technologies?.includes(tech))) return false;
    if (selectedHardware.length > 0 && !selectedHardware.some(hw => project.hardware?.includes(hw))) return false;
    return true;
  });

  const getCategoryIcon = (category: string) => {
    const icons: { [key: string]: string } = {
      'nature': '/kids/projects/nature-svgrepo-com.svg',
      'games': '/kids/projects/game-svgrepo-com.svg',
      'music': '/kids/projects/music-svgrepo-com.svg',
      'space': '/kids/projects/space-svgrepo-com.svg',
      'art': '/kids/projects/art-svgrepo-com.svg',
      'sports': '/kids/projects/sport-svgrepo-com.svg',
      'stories': '/kids/projects/stories-svgrepo-com.svg',
      'health': '/kids/projects/health-svgrepo-com.svg',
      'science': '/kids/projects/science-svgrepo-com.svg'
    };
    return icons[category] || '/kids/projects/nature-svgrepo-com.svg';
  };

  const getCategoryColor = (category: string) => {
    const colors: { [key: string]: string } = {
      'nature': 'bg-green-500',
      'games': 'bg-orange-500',
      'music': 'bg-pink-500',
      'space': 'bg-blue-500',
      'art': 'bg-yellow-500',
      'sports': 'bg-red-500',
      'stories': 'bg-purple-500',
      'health': 'bg-red-500',
      'science': 'bg-blue-500'
    };
    return colors[category] || 'bg-green-500';
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Top Banner */}
      <div className={`relative overflow-hidden`}>
        <div className={`p-8 relative z-10 ${getCategoryColor(category || 'nature')} rounded-lg mx-6 mt-20 mb-8`}>
          <div className="flex items-start justify-between">
            <div className="flex items-center space-x-4">
              <Link 
                to="/start-building"
                className="bg-white border border-black rounded-lg px-4 py-2 flex items-center space-x-2 hover:bg-gray-50 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                <span className="font-medium">Back</span>
              </Link>
              <div>
                <h1 className="text-4xl font-bold text-black mt-4" style={{fontFamily: 'Lexend, sans-serif'}}>
                  {category ? category.charAt(0).toUpperCase() + category.slice(1) : 'Category'}
                </h1>
              </div>
            </div>
            <div className="flex-shrink-0">
              <img 
                src={getCategoryIcon(category || 'nature')} 
                alt={category}
                className="w-16 h-16 object-contain"
              />
            </div>
          </div>
        </div>
        
        {/* Decorative Elements */}
        <div className="absolute top-4 right-4 opacity-20">
          <div className="flex flex-wrap gap-2">
            <div className="w-8 h-8 border-2 border-yellow-300 rounded-full"></div>
            <div className="w-6 h-6 border-2 border-yellow-300 rounded-full"></div>
            <div className="w-4 h-4 border-2 border-yellow-300 rounded-full"></div>
            <div className="w-6 h-6 border-2 border-yellow-300 rounded-full"></div>
            <div className="w-8 h-8 border-2 border-yellow-300 rounded-full"></div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-6 pb-8">
        <div className="flex gap-8">
          {/* Filter Sidebar */}
          <div className="w-64 flex-shrink-0">
            <div className="bg-white rounded-lg p-6 shadow-md">
              <h3 className="text-xl font-bold text-black mb-2" style={{fontFamily: 'Lexend, sans-serif'}}>
                Filter
              </h3>
              <p className="text-sm text-black mb-6" style={{fontFamily: 'Lexend, sans-serif'}}>
                The project list automatically updates when you apply a filter.
              </p>
              
              {/* PDF Only Filter */}
              <div className="mb-6">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={pdfOnly}
                    onChange={(e) => setPdfOnly(e.target.checked)}
                    className="w-4 h-4 border border-gray-300 rounded"
                  />
                  <span className="text-sm text-black" style={{fontFamily: 'Lexend, sans-serif'}}>PDF only</span>
                </label>
              </div>

              {/* Difficulty Level Section */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-bold text-black" style={{fontFamily: 'Lexend, sans-serif'}}>
                    Difficulty level
                  </h4>
                  <button
                    onClick={() => setIsDifficultyExpanded(!isDifficultyExpanded)}
                    className="text-black hover:text-gray-600 text-lg"
                  >
                    {isDifficultyExpanded ? '−' : '+'}
                  </button>
                </div>
                {isDifficultyExpanded && (
                  <div className="space-y-2">
                    {[1, 2, 3].map(level => (
                      <label key={level} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={difficultyLevels.includes(level)}
                          onChange={() => handleDifficultyChange(level)}
                          className="w-4 h-4 border border-gray-300 rounded"
                        />
                        <span className="text-sm text-black" style={{fontFamily: 'Lexend, sans-serif'}}>
                          Level {level}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Technology Section */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-bold text-black" style={{fontFamily: 'Lexend, sans-serif'}}>
                    Technology
                  </h4>
                  <button
                    onClick={() => setIsTechnologyExpanded(!isTechnologyExpanded)}
                    className="text-black hover:text-gray-600 text-lg"
                  >
                    {isTechnologyExpanded ? '−' : '+'}
                  </button>
                </div>
                {isTechnologyExpanded && (
                  <div className="space-y-2">
                    {['Scratch', 'Python', 'Web Development', 'AI & Data', 'Physical Computing', '3D & CAD'].map(tech => (
                      <label key={tech} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={selectedTechnologies.includes(tech)}
                          onChange={() => handleTechnologyChange(tech)}
                          className="w-4 h-4 border border-gray-300 rounded"
                        />
                        <span className="text-sm text-black" style={{fontFamily: 'Lexend, sans-serif'}}>
                          {tech}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>

              {/* Hardware Section */}
              <div className="mb-6">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-sm font-bold text-black" style={{fontFamily: 'Lexend, sans-serif'}}>
                    Hardware
                  </h4>
                  <button
                    onClick={() => setIsHardwareExpanded(!isHardwareExpanded)}
                    className="text-black hover:text-gray-600 text-lg"
                  >
                    {isHardwareExpanded ? '−' : '+'}
                  </button>
                </div>
                {isHardwareExpanded && (
                  <div className="space-y-2">
                    {['3D Printer', 'Electronic components', 'Raspberry Pi', 'Sense HAT'].map(hardware => (
                      <label key={hardware} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={selectedHardware.includes(hardware)}
                          onChange={() => handleHardwareChange(hardware)}
                          className="w-4 h-4 border border-gray-300 rounded"
                        />
                        <span className="text-sm text-black" style={{fontFamily: 'Lexend, sans-serif'}}>
                          {hardware}
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1">
            <div className="mb-6">
              <p className="text-sm font-bold text-gray-700" style={{fontFamily: 'Lexend, sans-serif'}}>
                {filteredProjects.length} results
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProjects.map(project => (
                <div key={project._id} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow overflow-hidden">
                  {/* Top Graphical Section */}
                  <div className="h-48 relative overflow-hidden">
                    <img 
                      src={getBannerImage(parseInt(project._id.slice(-2), 16) || 0)} 
                      alt={`${project.title} project illustration`}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  {/* Bottom Text Section */}
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-black" style={{fontFamily: 'Lexend, sans-serif'}}>
                        ☐☐☐ {project.platform} - Level {project.difficulty === 'Easy' ? '1' : project.difficulty === 'Medium' ? '2' : '3'}
                      </span>
                      <span className={`inline-block px-2 py-1 text-xs rounded-full ${
                        project.platform === 'Scratch' ? 'bg-orange-100 text-orange-800' :
                        project.platform === 'Python' ? 'bg-blue-100 text-blue-800' :
                        project.platform === 'Web Development' ? 'bg-green-100 text-green-800' :
                        project.platform === 'AI & Data' ? 'bg-purple-100 text-purple-800' :
                        project.platform === '3D & CAD' ? 'bg-pink-100 text-pink-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {project.platform}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-black mb-2 underline" style={{fontFamily: 'Lexend, sans-serif'}}>
                      {project.title}
                    </h3>
                    <p className="text-sm text-black mb-4" style={{fontFamily: 'Lexend, sans-serif'}}>
                      {project.description}
                    </p>
                    <Link 
                      to={`/project/${project._id}`}
                      className="flex items-center space-x-2 text-sm font-bold text-black hover:text-gray-600 transition-colors" 
                      style={{fontFamily: 'Lexend, sans-serif'}}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span>View Project</span>
                    </Link>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      
      {/* Project Detail Modal */}
      {isModalOpen && selectedProject && (
        <ProjectDetailModal
          project={selectedProject}
          isOpen={isModalOpen}
          onClose={handleCloseModal}
        />
      )}
    </div>
  );
};

export default CategoryDetail;
